import { useState } from "react";
import { Alert, Button, NumberInput, SectionCard } from "@/deps/ui-library";
import { useReceivePurchaseOrder } from "@/api/queries";
import type { PurchaseOrder } from "@/api/types";
import { DateField } from "@/components/DateField";

const today = () => new Date().toISOString().slice(0, 10);

const left = (line: PurchaseOrder["lines"][number]) =>
  Math.max(Number(line.quantity) - Number(line.received), 0);

/** What arrived, line by line. It adds to what's already there and can't be taken back (ADR-0055). */
export function ReceiveItemsCard({ order }: { order: PurchaseOrder }) {
  const receive = useReceivePurchaseOrder();
  const [date, setDate] = useState(today());
  const [typed, setTyped] = useState<Record<string, string>>({});
  const open = order.lines.filter((line) => left(line) > 0);

  const entered = open
    .map((line) => ({ lineId: line.id, quantity: typed[line.id] ?? "" }))
    .filter((line) => Number(line.quantity) > 0);
  const tooMany = open.some((line) => Number(typed[line.id] ?? 0) > left(line));

  return (
    <SectionCard title="Receive items">
      <div className="flex flex-col gap-3">
        {receive.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(receive.error as Error).message}
          />
        ) : null}
        {open.map((line) => (
          <div key={line.id} className="flex items-end justify-between gap-3">
            <div className="min-w-0 text-sm">
              <p className="text-fg truncate">{line.description}</p>
              <p className="text-fg-muted text-xs">
                Ordered <span className="num">{line.quantity}</span> · already received{" "}
                <span className="num">{line.received}</span>
              </p>
            </div>
            <div className="w-32 shrink-0">
              <NumberInput
                aria-label={`Received now, ${line.description}`}
                config={{ hideTriggers: true }}
                inputProps={{ className: "num pr-3 text-right" }}
                value={typed[line.id] ?? ""}
                onValueChange={(details: { value: string }) => {
                  if ((typed[line.id] ?? "") !== details.value) {
                    setTyped((now) => ({ ...now, [line.id]: details.value }));
                  }
                }}
              />
            </div>
          </div>
        ))}
        {tooMany ? (
          <p role="alert" className="text-bad-text text-xs">
            That is more than is left to arrive.
          </p>
        ) : null}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <DateField label="Received on" value={date} onChange={setDate} />
          <span className="flex gap-2">
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() =>
                setTyped(Object.fromEntries(open.map((line) => [line.id, String(left(line))])))
              }
            >
              Receive in full
            </Button>
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={entered.length === 0 || tooMany || date === "" || receive.isPending}
              onClick={() =>
                receive.mutate(
                  { id: order.id, receivedOn: date, lines: entered },
                  { onSuccess: () => setTyped({}) },
                )
              }
            >
              {receive.isPending ? "Saving" : "Record receipt"}
            </Button>
          </span>
        </div>
      </div>
    </SectionCard>
  );
}
